import { checkIsAcceptedFile } from "@/common/utils/fileValidator";
import { Download, Upload } from "lucide-react";
import { FormEventHandler, forwardRef, useId, useState } from "react"
import { twMerge } from "tailwind-merge";
import Spinner from "../Spinner";
import { cn } from "@/common/utils/cn";

export interface FileUploaderProps extends React.HTMLAttributes<HTMLInputElement> {
  id?: string;
  label?: string,
  name?: string,
  title?: string,
  fileName?: string
  currentFileUrl?: string,
  accept?: string
  className?: string
  handleChange?: (file: File, blobId?: string | undefined) => void
  disabled?: boolean
  currentFilePlaceholderClassName?: string;
  currentFilePlaceholderFileNameClassName?: string;
  currentFilePlaceholderIcon?: JSX.Element;
  containerClassName?: string;
  titleClassName?: string;
  isLoading?: boolean;
  dataTestid?: string;
  maxFileSizeInMb?: number;
  setError?: (errorMessage?: string) => void
  uploadBlob?: (data: { file: File; filename: string }) => Promise<any>
}

export const FileUploader = forwardRef<HTMLInputElement, FileUploaderProps>(({
  id,
  label,
  name,
  title = 'Anexe um arquivo',
  fileName,
  currentFileUrl,
  className,
  accept = '',
  handleChange,
  onChange,
  currentFilePlaceholderClassName,
  currentFilePlaceholderFileNameClassName,
  currentFilePlaceholderIcon,
  containerClassName,
  titleClassName,
  isLoading,
  dataTestid,
  maxFileSizeInMb = 20,
  setError,
  uploadBlob,
  ...props
}, ref) => {
  const uniqueId = useId();

  const [dragActive, setDragActive] = useState(false);

  const handleOnChange: FormEventHandler<HTMLInputElement> = async (event) => {
    onChange?.(event)

    if (handleChange) {
      const file = (event.target as HTMLInputElement & { files: FileList }).files?.[0]

      if (!file || !checkIsAcceptedFile(file, accept)) {
        return
      }

      if (maxFileSizeInMb && setError) {
        const size = file.size;

        if (size > maxFileSizeInMb * 1024 * 1024) {
          (event.target as HTMLInputElement).value = '';

          const fileSizeInMb = (size / 1024 / 1024).toFixed(1)

          setError?.(`O arquivo excede o tamanho de ${maxFileSizeInMb}MB. (Tamanho do arquivo: ${fileSizeInMb}MB)`)

          return;
        } else {
          setError?.(undefined);
        }
      }

      let blobId: string | undefined = undefined;

      if (uploadBlob) {
        const data = await uploadBlob({ file, filename: file.name })

        blobId = data?.data?.data?.id;
      }

      handleChange(file, blobId)
    }
  }

  return (
    <div className={twMerge('flex flex-col items-start', containerClassName)}>
      {label && (
        <p className="text-xs mb-1">
          {label}
        </p>
      )}

      <label
        htmlFor={id || uniqueId || "file_input"}
        className={cn(
          "relative",
          currentFileUrl && "flex flex-col justify-between p-4 min-h-[100px] bg-white-200 rounded-lg",
          !currentFileUrl && "flex flex-col justify-center border border-neutral-3 w-[180px] h-[100px] gap-2 items-center bg-neutral-1 cursor-pointer rounded-lg",
          className,
          dragActive && "bg-blue-100/30 border-blue-300",
        )}
        onDragLeave={e => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragActive(false);
          }
        }}
        onDragEnter={e => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragOver={e => {
          e.preventDefault();
        }}
        onDrop={e => {
          e.preventDefault();
          setDragActive(false);
          handleOnChange({ target: { files: e.dataTransfer.files } } as any);
        }}
      >
        {currentFileUrl ? (
          <>
            {isLoading && (
              <div className="w-full flex justify-end">
                <Spinner className="fill-white-400" />
              </div>
            )}

            <div className={currentFilePlaceholderClassName}>
              {currentFilePlaceholderIcon}

              <div className={cn(
                "w-full flex justify-start italic text-xs overflow-hidden text-ellipsis break-all",
                currentFilePlaceholderFileNameClassName
              )}
              >
                {fileName || 'Documento'}
              </div>
            </div>

            <div className="w-full flex justify-end">
              <a
                href={currentFileUrl}
                download={fileName}
                target="_blank"
                data-testid={(dataTestid || id || uniqueId || "file_input") + '_link'}
              >
                <Download />
              </a>
            </div>
          </>
        ) : (
          <>
            {isLoading && (
              <div className="w-full flex justify-end absolute top-2 right-2">
                <Spinner className="fill-white-400 size-5" />
              </div>
            )}

            <p className={cn("text-xs", titleClassName)}>
              {title}
            </p>

            <Upload />

            <p className="text-xs italic">
              {dragActive ? "Solte aqui" : "Ou arraste até aqui"}
            </p>
          </>
        )}
      </label>

      <input
        type="file"
        ref={ref}
        name={name}
        className="hidden"
        id={id || uniqueId || "file_input"}
        accept={accept}
        onChange={handleOnChange}
        data-testid={dataTestid}
        {...props}
      />
    </div>
  )
})
